import { Dispatch, SetStateAction } from 'react';
import { IWishlist } from '@monofront/types';
import { Channel } from '../../constants';

/**
 * IWishlistCRUD is used for wishlists actions
 * these actions will handle link with related database (e.q firestore for ecommerce repo)
 */
export interface IWishlistCRUD {
  create: (url: string, data: Record<string, any>) => any;
  // no read, listen is enough for this job
  update: (url: string, wishlistUid: string, data: Record<string, any>) => any;
  delete: (url: string, wishlistUid: string) => any;
  listen: (
    url: string,
    channel: Channel,
    callback: (wishlists: IWishlist[]) => void,
  ) => any;
}

/**
 * This class is meant to handle wishlists actions
 * @param wishlistCRUD {IWishlistCRUD} service for wishlist actions
 */
export class WishlistService {
  private wishlistCRUD: IWishlistCRUD;

  constructor(crudService: IWishlistCRUD) {
    this.wishlistCRUD = crudService;
  }

  /**
   * Create a new wishlist
   * @param customerUid {string} customer uid
   * @param wishlistName {string} name of the new wishlist
   * @param wishlistProducts {string[]} objectId of all products to add in this wishlist
   * @returns wishlistUid {Promise<string>} wishlist uid
   */
  public createWishlist = async (
    customerUid: string,
    wishlistName: string,
    wishlistProducts: string[] = [],
  ): Promise<string> => {
    const { id: wishlistUid } = await this.wishlistCRUD.create(customerUid, {
      name: wishlistName,
      products: wishlistProducts,
    });

    return wishlistUid;
  };

  /**
   * Delete a wishlist
   * @param customerUid {string} customer uid
   * @param wishlistUid {string} uid of the related wishlist
   * @returns {Promise<void>}
   */
  public deleteWishlist = async (
    customerUid: string,
    wishlistUid: string,
  ): Promise<void> => {
    await this.wishlistCRUD.delete(customerUid, wishlistUid);
  };

  /**
   * Rename the wishlist
   * @param customerUid {string} customer uid
   * @param wishlistUid {string} uid of the related wishlist
   * @param wishlistName {string} new name of the new wishlist
   * @returns {Promise<void>}
   */
  public renameWishlist = async (
    customerUid: string,
    wishlistUid: string,
    wishlistName: string,
  ): Promise<void> => {
    await this.wishlistCRUD.update(customerUid, wishlistUid, {
      name: wishlistName,
    });
  };

  /**
   * Add product to a wishlist
   * @param customerUid {string} customer uid
   * @param wishlistUid {string} uid of the related wishlist
   * @param currentProductsInWishlist {string[]} objectId of all products saved in the wishlist
   * @param objectId {string} objectId of the product to add
   * @returns {Promise<void>}
   */
  public addProductToWishlist = async (
    customerUid: string,
    wishlistUid: string,
    currentProductsInWishlist: string[],
    objectId: string,
  ): Promise<void> => {
    await this.wishlistCRUD.update(customerUid, wishlistUid, {
      products: [...currentProductsInWishlist, objectId],
    });
  };

  /**
   * Delete product from a wishlist
   * @param customerUid {string} customer uid
   * @param wishlistUid {string} uid of the related wishlist
   * @param currentProductsInWishlist {string[]} objectId of all products saved in the wishlist
   * @param objectId {string} objectId of the product to delete
   * @returns {Promise<void>}
   */
  public deleteProductFromWishlist = async (
    customerUid: string,
    wishlistUid: string,
    currentProductsInWishlist: string[],
    objectId: string,
  ): Promise<void> => {
    const products =
      currentProductsInWishlist?.filter((curr) => curr !== objectId) || [];

    await this.wishlistCRUD.update(customerUid, wishlistUid, { products });
  };

  /**
   * Delete all products from a wishlist
   * @param customerUid {string} customer uid
   * @param wishlistUid {string} uid of the related wishlist
   * @returns {Promise<void>}
   */
  public deleteAllProductsFromWishlist = async (
    customerUid: string,
    wishlistUid: string,
  ): Promise<void> => {
    await this.wishlistCRUD.update(customerUid, wishlistUid, { products: [] });
  };

  /**
   * Wishlist listener. Both listener are used in component or hook
   * @param customerUid {string} customer uid
   * @param updateWishlistState {Dispatch<SetStateAction<IWishlist[]>>} wishlist state
   * @param updateLoadingState {Dispatch<SetStateAction<boolean>>} loading state
   * @returns {any}
   */
  public listenToWishlist = (
    customerUid: string,
    channel: Channel,
    updateWishlistState: Dispatch<SetStateAction<IWishlist[]>>,
    updateLoadingState: Dispatch<SetStateAction<boolean>>,
  ) => {
    return this.wishlistCRUD.listen(customerUid, channel, (wishlists) => {
      updateWishlistState(wishlists as IWishlist[]);
      updateLoadingState(false);
    });
  };
}
